// lib/firebase/auth.ts
import { 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
  sendPasswordResetEmail,
  UserCredential,
  User as FirebaseUser
} from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db } from './config';
import { User } from '../../types';

// Interface étendue pour l'inscription
export interface RegisterData {
  email: string;
  password: string;
  displayName: string;
  role: 'supervisor' | 'secretary';
  institutionId?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
}

export const registerUser = async ({
  email,
  password,
  displayName,
  role,
  institutionId,
  firstName,
  lastName,
  phone
}: RegisterData): Promise<UserCredential> => {
  try {
    // 1. Créer l'utilisateur avec Firebase Auth
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // 2. Mettre à jour le profil avec displayName
    await updateProfile(user, {
      displayName: displayName
    });

    // 3. Écrire les données utilisateur dans Firestore
    const userDocRef = doc(db, 'users', user.uid);
    
    await setDoc(userDocRef, {
      uid: user.uid,
      email: email,
      displayName: displayName,
      firstName: firstName || displayName.split(' ')[0] || '',
      lastName: lastName || displayName.split(' ').slice(1).join(' ') || '',
      phone: phone || '',
      role: role,
      institutionId: role === 'secretary' ? institutionId : null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      emailVerified: user.emailVerified,
      isActive: true,
      hasCompletedProfile: false,
      lastLoginAt: null,
      metadata: {
        creationTime: user.metadata.creationTime,
        lastSignInTime: user.metadata.lastSignInTime
      }
    });

    // 4. Si c'est un secrétaire, créer une entrée dans la sous-collection de l'institution
    if (role === 'secretary' && institutionId) {
      const secretaryRef = doc(db, 'institutions', institutionId, 'secretaries', user.uid);
      await setDoc(secretaryRef, {
        uid: user.uid,
        email: email,
        displayName: displayName,
        status: 'pending',
        addedAt: new Date().toISOString(),
        addedBy: 'system',
        isActive: true
      });
    }

    return userCredential;

  } catch (error: any) {
    console.error('Registration error:', error);
    
    // Gérer les erreurs spécifiques
    let errorMessage = 'Une erreur est survenue lors de l\'inscription';
    
    switch (error.code) {
      case 'auth/email-already-in-use':
        errorMessage = 'Cette adresse email est déjà utilisée';
        break;
      case 'auth/weak-password':
        errorMessage = 'Le mot de passe est trop faible (minimum 6 caractères)';
        break;
      case 'auth/invalid-email':
        errorMessage = 'Adresse email invalide';
        break;
      case 'auth/operation-not-allowed':
        errorMessage = 'L\'inscription par email/mot de passe n\'est pas activée';
        break;
      default:
        errorMessage = error.message || errorMessage;
    }
    
    throw new Error(errorMessage);
  }
};

export const loginUser = async (email: string, password: string): Promise<UserCredential> => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    
    // Mettre à jour lastLoginAt dans Firestore
    const user = userCredential.user;
    const userDocRef = doc(db, 'users', user.uid);
    
    await setDoc(userDocRef, {
      lastLoginAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }, { merge: true });
    
    return userCredential;
  } catch (error: any) {
    console.error('Login error:', error);
    
    let errorMessage = 'Échec de la connexion';
    switch (error.code) {
      case 'auth/user-not-found':
      case 'auth/wrong-password':
        errorMessage = 'Email ou mot de passe incorrect';
        break;
      case 'auth/user-disabled':
        errorMessage = 'Ce compte a été désactivé';
        break;
      case 'auth/too-many-requests':
        errorMessage = 'Trop de tentatives. Veuillez réessayer plus tard';
        break;
      default:
        errorMessage = error.message || errorMessage;
    }
    
    throw new Error(errorMessage);
  }
};

export const logoutUser = async (): Promise<void> => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error('Logout error:', error);
    throw error;
  }
};

export const getCurrentUser = (): FirebaseUser | null => {
  return auth.currentUser;
};

export const getUserData = async (uid: string): Promise<User> => {
  try {
    const userDoc = await doc(db, 'users', uid);
    const userSnap = await getDoc(userDoc);
    
    if (!userSnap.exists()) {
      throw new Error('User data not found');
    }
    
    return userSnap.data() as User;
  } catch (error) {
    console.error('Error getting user data:', error);
    throw error;
  }
};

export const updateUserProfile = async (uid: string, data: Partial<User>): Promise<void> => {
  try {
    const userDocRef = doc(db, 'users', uid);
    
    await setDoc(userDocRef, {
      ...data,
      updatedAt: new Date().toISOString()
    }, { merge: true });
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
};

export const resetPassword = async (email: string): Promise<void> => {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error: any) {
    console.error('Reset password error:', error);
    
    let errorMessage = 'Erreur lors de l\'envoi de l\'email de réinitialisation';
    switch (error.code) {
      case 'auth/user-not-found':
        errorMessage = 'Aucun utilisateur trouvé avec cet email';
        break;
      case 'auth/invalid-email':
        errorMessage = 'Adresse email invalide';
        break;
      default:
        errorMessage = error.message || errorMessage;
    }
    
    throw new Error(errorMessage);
  }
};